import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App } from './App';
import { ProveedorAuth } from './lib/auth';
import './index.css';

const cliente = new QueryClient({
  defaultOptions: {
    queries: {
      /**
       * No se reintenta lo que el servidor ya respondió con un 4xx.
       *
       * ⚠️ Con `retry: 1` a secas, un **404 se reintentaba**, y como el reintento
       * espera antes de correr, la consulta se quedaba en `isPending` todo ese
       * rato: la pantalla de detalle de un cliente inexistente mostraba
       * «Cargando…» y no llegaba nunca a decir «no lo encontramos». Se vio en el
       * navegador, no en las pruebas — las pruebas llaman a la API, no a la
       * pantalla.
       *
       * Y reintentar un 4xx no tiene sentido igual: 404, 403 y 422 son la
       * respuesta definitiva del servidor, no una falla de red. Lo que sí se
       * reintenta es el 5xx y el fallo de conexión, que es lo que puede mejorar
       * solo. El 401 lo maneja aparte `api.ts`, renovando el token.
       */
      retry: (intentos, error) => {
        const estado = (error as { estado?: number }).estado;
        if (estado !== undefined && estado >= 400 && estado < 500) return false;
        return intentos < 1;
      },
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});

const raiz = document.getElementById('raiz');
if (!raiz) throw new Error('Falta el elemento #raiz en index.html');

createRoot(raiz).render(
  <StrictMode>
    <QueryClientProvider client={cliente}>
      <BrowserRouter>
        <ProveedorAuth>
          <App />
        </ProveedorAuth>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
